import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingFallback } from '../common/LoadingFallback';
import { ConnectButton } from '../navigation/ConnectButton';
import { ConstellationLoader } from '../common/ConstellationLoader';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'hana-viewer': {
        url: string;
        style?: React.CSSProperties;
        loading?: string;
        onLoad?: () => void;
        onError?: () => void;
      };
    }
  }
}

export const LandingPage = () => {
  const {
    user,
    profile,
    isLoading: authLoading,
    isInitialized
  } = useAuth();
  
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isSceneLoaded, setIsSceneLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showCustomLoader, setShowCustomLoader] = useState(true);
  
  console.log('LandingPage - State:', {
    authLoading,
    isInitialized,
    user,
    profile,
    hasCompletedOnboarding: profile?.has_completed_onboarding,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    const loadScript = async () => {
      try {
        // Check if script is already loaded
        if (document.querySelector('script[src*="hana-viewer"]')) {
          setIsScriptLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://cdn.spline.design/@splinetool/hana-viewer@1.0.75/hana-viewer.js';
        
        script.onload = () => setIsScriptLoaded(true);
        script.onerror = () => setLoadError(true);
        
        document.head.appendChild(script);
      } catch (error) {
        console.error('Failed to load Hana viewer script:', error);
        setLoadError(true);
      }
    };

    loadScript();
  }, []);

  // Use a timeout to assume scene has loaded after script loads
  useEffect(() => {
    if (isScriptLoaded && !isSceneLoaded) {
      const timer = setTimeout(() => {
        setIsSceneLoaded(true);
        // Hide custom loader after scene loads with a small delay for smooth transition
        setTimeout(() => setShowCustomLoader(false), 800);
      }, 2000); // Give 2 seconds for scene to load after script

      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded, isSceneLoaded]);

  // Failsafe: even if script never loads, ensure UI (login button) is visible after 3s
  useEffect(() => {
    const failsafe = setTimeout(() => {
      if (!isSceneLoaded) {
        setIsSceneLoaded(true);
        setShowCustomLoader(false);
      }
    }, 3000);
    return () => clearTimeout(failsafe);
  }, [isSceneLoaded]);

  if (!isInitialized) {
    console.log('LandingPage - Waiting for auth initialization');
    return <LoadingFallback />;
  }

  if (authLoading) {
    console.log('LandingPage - Auth is loading');
    return <LoadingFallback />;
  }

  if (user && profile) {
    // Primary check: has user completed onboarding?
    if (profile.has_completed_onboarding) {
      console.log('LandingPage - Has completed onboarding, redirecting to bulletins');
      return <Navigate to="/bulletins" replace />;
    }
    
    // User has NOT completed onboarding → send to username creation
    if (!profile.has_completed_onboarding) {
      console.log('LandingPage - Has NOT completed onboarding, redirecting to create-username');
      return <Navigate to="/create-username" replace />;
    }
  }
  
  if (user && !profile && authLoading) {
    console.log('LandingPage - Profile data still loading');
    return <LoadingFallback />;
  }

  if (loadError) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Unable to load 3D scene</p>
          <div className="absolute inset-0 flex items-end justify-center pb-12 z-10">
            <ConnectButton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Constellation Loading Animation - Always render, control with opacity */}
      <ConstellationLoader isVisible={showCustomLoader} />

      {/* Hana Background */}
      <hana-viewer 
        url="https://prod.spline.design/EMTaj3OjlBUxef1F-t8z/scene.hanacode"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          opacity: isSceneLoaded ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out'
        }}
        loading="eager"
      />
      
      {/* Login Button Overlay */}
      <div 
        className="absolute inset-0 flex items-end justify-center pb-12 z-50"
      >
        <div 
          className={`transition-opacity duration-700 ${isSceneLoaded ? 'opacity-100' : 'opacity-80'}`}
        >
          <ConnectButton />
        </div>
      </div>
    </div>
  );
};
